/**
 * Auto-apply controllers and hooks
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useRelayEnvironment } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type ContentContext,
	useChatContextEditorContext,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import type { AutoApplyState, SessionData } from '../../common/types';
import {
	getAutoApplyState,
	getAutoApplyStateWithSession,
	setAutoApplyState,
	setAutoApplyStateWithSession,
} from '../../common/utils/auto-apply-storage';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Safely gets the Relay environment without throwing if no provider is available.
 * This allows the hook to work in test environments or contexts without RelayEnvironmentProvider.
 */
const useSafeRelayEnvironment = (): IEnvironment | null => {
	try {
		return useRelayEnvironment();
	} catch {
		// No RelayEnvironmentProvider available, return null
		// The hook will fall back to localStorage-only mode
		return null;
	}
};

// ============================================================================
// Types
// ============================================================================

export type AutoApplyAvailable = {
	type: 'available';
};

export type AutoApplyUnavailable = {
	type: 'unavailable';
	reason: 'no_editor_context' | 'agentic_conversation' | 'no_page_context';
};

export type AutoApplyAvailabilityType = AutoApplyAvailable | AutoApplyUnavailable;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook that determines if auto-apply functionality is available based on context
 */
export const useAutoApplyAvailability = (
	contentContext: ContentContext,
	useCurrentPageContext: boolean,
	isConversationAgentic: boolean,
	productKey?: 'jira' | 'confluence' | string,
): { autoApplyAvailability: AutoApplyAvailabilityType } => {
	// Get editor context to determine if there's an active editor to stream to
	const [editorContext] = useChatContextEditorContext();

	let autoApplyAvailability: AutoApplyAvailabilityType = {
		type: 'unavailable',
		reason: 'no_editor_context',
	};

	// Agentic conversations handle their own application
	if (isConversationAgentic) {
		autoApplyAvailability = { type: 'unavailable', reason: 'agentic_conversation' };
	}
	// Show toggle in staging-area
	// staging-area has its own page context, so we check this before the useCurrentPageContext check
	else if (contentContext === 'staging-area') {
		autoApplyAvailability = { type: 'available' };
	}
	// Must have current page context to apply content to
	else if (!useCurrentPageContext) {
		autoApplyAvailability = { type: 'unavailable', reason: 'no_page_context' };
	}
	// For Jira, always show the toggle in global context (no editor context required)
	else if (contentContext === 'global' && productKey === 'jira') {
		autoApplyAvailability = { type: 'available' };
	}
	// For Confluence and other products in global context, need active editor to stream to
	else if (contentContext === 'global' && Boolean(editorContext)) {
		autoApplyAvailability = { type: 'available' };
	}

	return {
		autoApplyAvailability,
	};
};

/**
 * Hook that provides auto-apply state with backend persistence and localStorage fallback
 * This hook handles both backend calls (when session data is available) and localStorage fallback
 */
export const useAutoApplyState = (
	sessionData?: SessionData,
	_contentContext?: ContentContext,
	_useCurrentPageContext?: boolean,
	_isConversationAgentic?: boolean,
): AutoApplyState => {
	const environment = useSafeRelayEnvironment();
	const experienceTracker = useAIMateExperienceTracker();
	const [enabled, setEnabledState] = useState<boolean>(() => getAutoApplyState());
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	// Cache the current state to avoid unnecessary re-renders
	const stateRef = useRef(enabled);
	stateRef.current = enabled;

	// Determine if we should make backend calls (need environment + session + feature gate)
	const shouldUseBackend =
		environment && sessionData && sessionData.userId && fg('enable_auto_apply_toggling');

	// Determine if we should use localStorage fallback
	const shouldUseLocalStorage =
		(!sessionData || !sessionData.userId || !environment) && fg('enable_auto_apply_toggling');

	// If feature gate is off, don't provide any auto-apply functionality
	const isFeatureDisabled = !fg('enable_auto_apply_toggling');

	// Load state from backend when component mounts or sessionData changes
	const refresh = useCallback(async (): Promise<void> => {
		if (isFeatureDisabled) {
			return;
		}

		if (shouldUseBackend) {
			const experienceId = `${ExperienceName.GET_AUTO_APPLY_STATE}-${Date.now()}`;
			addSentryBreadcrumb(
				`useAutoApplyState: fetching state from backend for user ${sessionData!.userId}`,
			);
			experienceTracker.start({
				id: experienceId,
				name: ExperienceName.GET_AUTO_APPLY_STATE,
			});

			setLoading(true);
			setError(null);
			try {
				const backendState = await getAutoApplyStateWithSession(environment, sessionData!);
				addSentryBreadcrumb(`useAutoApplyState: backend returned autoApply=${backendState}`);
				if (backendState !== stateRef.current) {
					setEnabledState(backendState);
				}
				experienceTracker.succeed({
					name: ExperienceName.GET_AUTO_APPLY_STATE,
				});
			} catch (err) {
				// Store error and keep current state - don't fall back to localStorage
				const error = err instanceof Error ? err : new Error('Failed to fetch auto-apply state');
				addSentryBreadcrumb(`useAutoApplyState: failed to fetch state - ${error.message}`);
				setError(error);
				experienceTracker.fail({
					name: ExperienceName.GET_AUTO_APPLY_STATE,
					error,
				});
			} finally {
				setLoading(false);
			}
		} else if (shouldUseLocalStorage) {
			const localState = getAutoApplyState();
			if (localState !== stateRef.current) {
				setEnabledState(localState);
			}
		}
	}, [
		environment,
		sessionData,
		shouldUseBackend,
		shouldUseLocalStorage,
		isFeatureDisabled,
		experienceTracker,
	]);

	// Set state with backend persistence
	const setEnabled = useCallback(
		async (newEnabled: boolean): Promise<void> => {
			if (isFeatureDisabled) {
				return;
			}

			if (newEnabled === stateRef.current) {
				return; // No change needed
			}

			const experienceId = `${ExperienceName.SET_AUTO_APPLY_STATE}-${Date.now()}`;

			setLoading(true);
			setError(null);
			try {
				if (shouldUseBackend) {
					addSentryBreadcrumb(`useAutoApplyState: setting state to ${newEnabled} via backend`);
					experienceTracker.start({
						id: experienceId,
						name: ExperienceName.SET_AUTO_APPLY_STATE,
					});
					await setAutoApplyStateWithSession(environment, sessionData!, newEnabled);
					addSentryBreadcrumb(`useAutoApplyState: successfully set autoApply=${newEnabled}`);
					experienceTracker.succeed({
						name: ExperienceName.SET_AUTO_APPLY_STATE,
						attributes: {
							autoApplyEnabled: newEnabled,
						},
					});
				} else if (shouldUseLocalStorage) {
					addSentryBreadcrumb(`useAutoApplyState: setting state to ${newEnabled} via localStorage`);
					setAutoApplyState(newEnabled);
				}
				setEnabledState(newEnabled);
			} catch (err) {
				// Store error and keep current state, don't update UI
				const error = err instanceof Error ? err : new Error('Failed to update auto-apply state');
				addSentryBreadcrumb(`useAutoApplyState: failed to set state - ${error.message}`);
				setError(error);
				if (shouldUseBackend) {
					experienceTracker.fail({
						name: ExperienceName.SET_AUTO_APPLY_STATE,
						error,
						attributes: {
							autoApplyEnabled: newEnabled,
						},
					});
				}
				throw error; // Re-throw so the component can handle it
			} finally {
				setLoading(false);
			}
		},
		[
			environment,
			sessionData,
			shouldUseBackend,
			shouldUseLocalStorage,
			isFeatureDisabled,
			experienceTracker,
		],
	);

	// Initialize state from backend
	useEffect(() => {
		void refresh();
	}, [refresh]);

	return {
		enabled: isFeatureDisabled ? false : enabled, // Always disabled when feature gate is off
		loading: isFeatureDisabled ? false : loading, // Never loading when disabled
		error: isFeatureDisabled ? null : error, // No error when disabled
		setEnabled,
		refresh,
	};
};
